import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { setPageHeaderTextActionCreator } from 'plugins/store/reducers/common.reducer';
import { setPortalSettingsActionCreator } from 'plugins/store/reducers/settings.reducer';
import { setAutoLockSettingsActionCreator } from 'plugins/store/reducers/auth.reducer';
import { Controller } from 'react-hook-form';
import withForm from 'hoc/withForm';
import { withSnackbar } from 'notistack';
import { getPortalSettings, updatePortalSettings } from 'services/portal-settings.service';
import { mapSettingsToObject } from 'mappings/portalSettings.mapping';
import { countries as availableCountries } from 'utils/enums/countries.enum';

// @material-ui/core components
import Autocomplete from '@material-ui/lab/Autocomplete';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
// @material-ui/icons
import SettingsOutlined from '@material-ui/icons/SettingsOutlined';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import Button from 'components/CustomButtons/Button';
import CardIcon from 'components/Card/CardIcon';
import CustomInput from 'components/CustomInput/CustomInput';
import SnackMessage from 'components/Snackbar/SnackMessage';
import CustomTextField from 'components/CustomTextField/CustomTextField';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import RouteLeavingGuard from 'components/RouteLeavingGuard/RouteLeavingGuard';

import styles from 'assets/jss/material-dashboard-pro-react/views/configurationStyle';

class Configuration extends Component {

	state = {
		loading: true,
		allowedCountries: [],
		enableFraudProtection: 'true',
	};

	viewName = 'Configuration';

	componentDidMount() {
		this.props.setPageHeaderText(this.viewName);
		this.getPortalSettings();

	}

	componentWillUnmount() {
		this.props.setPageHeaderText('');
	}

	getPortalSettings = async () => {
		const {
			enqueueSnackbar,
			formMethods: { reset },
		} = this.props;

		try {
			this.setState({
				loading: true,
			});

			const settings = await getPortalSettings();
			const portalSettings = mapSettingsToObject(settings);

			this.setState({ allowedCountries: portalSettings?.allowedCountryForPhoneNumbers || [] });

			this.setState({ enableFraudProtection: portalSettings?.enableFraudProtection || 'true' });

			reset(portalSettings);
		} catch (e) {
			enqueueSnackbar(e.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}

		this.setState({
			loading: false,
		});
	}

	onSubmit = async (data) => {

		data.organizationName && (data.organizationName = data.organizationName.trim());
		data.defaultEndCallNotification && (data.defaultEndCallNotification = data.defaultEndCallNotification.trim());
		data.allowedCountryForPhoneNumbers
			&& (data.allowedCountryForPhoneNumbers = data.allowedCountryForPhoneNumbers.map(({ code }) => code).join(';'));
		data.country && (data.country = data.country?.code);
		const {
			enqueueSnackbar,
			setPortalSettings,
			setAutoLockSettings,
			formMethods: { reset },
		} = this.props;

		try {
			const settings = await updatePortalSettings(data);
			const portalSettings = mapSettingsToObject(settings);

			setPortalSettings(portalSettings);

			setAutoLockSettings({
				warningTimeout: portalSettings.autoLogoutNotificationTimeout,
				lockTimeout: portalSettings.autoLogoutTimeout,
			});

			reset(portalSettings);

			enqueueSnackbar('Changes have been saved successfully', {
				content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
			});
		} catch (e) {
			enqueueSnackbar(e.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	};

	convertToIntValue(value) {
		return parseInt(value) || '';
	}

	render() {
		const { classes, formMethods, history } = this.props;

		const { control, trigger, handleSubmit, formState, getValues, setValue } = formMethods;

		const { errors, isDirty, isSubmitting } = formState;

		const validation = {
			autoLogoutNotificationTimeout: {
				min: 1,
				max: 1440,
			},
			autoLogoutTimeout: {
				min: 1,
				max: 60,
			},
			organizationName: {
				maxLength: 255,
			},
			pushNotificationTimer: {
				min: 1,
				max: 3600,
			},
			smsPushNotificationTimer: {
				min: 1,
				max: 3600,
			},
			defaultEndCallNotification: {
				maxLength: 255,
			},
			keepNotificationsPeriodInDays: {
				min: 31,
				max: 365,
			},
			contactCenterNameMaxLength: {
				min: 1,
				max: 255,
			},
			defaultNotificationMaxLength: {
				min: 1,
				max: 255,
			},
			unverifiedCallArrivalNotificationMaxLength: {
				min: 1,
				max: 255,
			},
			customNotificationMaxLength: {
				min: 1,
				max: 255,
			},
			keepCallsPeriodInDays: {
				min: 31,
				max: 365,
			},
			refreshAnalyticsDataIntervalInSeconds: {
				min: 5,
				max: 3600,
			},
			keepRatingsPeriodInDays: {
				min: 1,
				max: 365,
			},
			refreshThresholdsIntervalInMinutes: {
				min: 5,
				max: 60,
			},
		};

		return (
			this.state.loading
				? <div className={classes.loadingIndicator}><LoadingIndicator /></div>
				: <>
					<GridContainer>
						<GridItem xs={12} sm={12} md={12}>
							<Card>
								<CardHeader color="primary" icon>
									<CardIcon color="primary">
										<SettingsOutlined />
									</CardIcon>
								</CardHeader>
								<CardBody>
									<form onSubmit={handleSubmit(this.onSubmit)}>
										<GridContainer>
											<GridItem md={12}>
												<h4 className={classes.subTitle}>General</h4>
											</GridItem>
											<GridItem xs={12} sm={12} md={12}>
												<Controller
													name="organizationName"
													control={control}
													defaultValue={''}
													rules={{
														required: 'Field should not be empty',
														maxLength: {
															value: validation.organizationName.maxLength,
															message: `Maximum ${validation.organizationName.maxLength} characters`
														}
													}}
													render={(props) => (
														<CustomInput
															labelText="Organization Name *"
															error={!!errors.organizationName}
															helperText={errors.organizationName ? errors.organizationName.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																value: props.value,
																type: "text"
															}}
															onChange={(event) => {
																const value = event.target.value?.trimStart();
																props.onChange(value);
																trigger('organizationName');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="pushNotificationTimer"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.pushNotificationTimer.min,
															message: `Min value is ${validation.pushNotificationTimer.min}`
														},
														max: {
															value: validation.pushNotificationTimer.max,
															message: `Max value is ${validation.pushNotificationTimer.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Universal Notification Timer *'
															error={!!errors.pushNotificationTimer}
															helperText={errors.pushNotificationTimer ? errors.pushNotificationTimer.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: 1,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('pushNotificationTimer');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
                                                                                                <Controller
                                                                                                        name="smsPushNotificationTimer"
                                                                                                        control={control}
                                                                                                        defaultValue={0}
                                                                                                        rules={{
                                                                                                                required: 'Field should not be empty',
                                                                                                                min: {
                                                                                                                        value: validation.smsPushNotificationTimer.min,
                                                                                                                        message: `Min value is ${validation.smsPushNotificationTimer.min}`
                                                                                                                },
                                                                                                                max: {
                                                                                                                        value: validation.smsPushNotificationTimer.max,
                                                                                                                        message: `Max value is ${validation.smsPushNotificationTimer.max}`
                                                                                                                },
                                                                                                        }}
                                                                                                        render={(props) => (
                                                                                                                <CustomInput
                                                                                                                        labelText='SMS Expiration Timer *'
                                                                                                                        error={!!errors.smsPushNotificationTimer}
                                                                                                                        helperText={errors.smsPushNotificationTimer ? errors.smsPushNotificationTimer.message : ""}
                                                                                                                        formControlProps={{
                                                                                                                                fullWidth: true
                                                                                                                        }}
                                                                                                                        inputProps={{
                                                                                                                                type: 'number',
                                                                                                                                min: 1,
                                                                                                                                value: props.value,
                                                                                                                        }}
                                                                                                                        onChange={(event) => {
                                                                                                                                const value = this.convertToIntValue(event.target.value);
                                                                                                                                event.target.value = value;
                                                                                                                                props.onChange(value);
                                                                                                                                trigger('smsPushNotificationTimer');
                                                                                                                        }}
                                                                                                                />
                                                                                                        )}
                                                                                                />
                                                                                        </GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="defaultEndCallNotification"
													control={control}
													defaultValue={''}
													rules={{
														required: 'Field should not be empty',
														maxLength: {
															value: validation.defaultEndCallNotification.maxLength,
															message: `Maximum ${validation.defaultEndCallNotification.maxLength} characters`
														}
													}}
													render={(props) => (
														<CustomInput
															labelText='End Call Notification *'
															error={!!errors.defaultEndCallNotification}
															helperText={errors.defaultEndCallNotification ? errors.defaultEndCallNotification.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'text',
																value: props.value,
															}}
															onChange={(event) => {
																const value = event.target.value?.trimStart();
																props.onChange(value);
																trigger('defaultEndCallNotification');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="allowedCountryForPhoneNumbers"
													control={control}
													defaultValue={[]}
													rules={{
														required: 'Field should not be empty',
													}}
													render={({ onChange, value }) => {
														return (
															<Autocomplete
																multiple
																options={availableCountries}
																autoComplete
																includeInputInList
																value={value}
																getOptionLabel={({ label }) => label}
																renderInput={(params) =>
																	<CustomTextField
																		{...params}
																		autocomplete="off"
																		error={!!errors.allowedCountryForPhoneNumbers}
																		helperText={errors.allowedCountryForPhoneNumbers ? errors.allowedCountryForPhoneNumbers.message : ""}
																		label="Available Countries *"
																		margin="normal"
																	/>
																}
																onChange={(e, val) => {
																	onChange(val);
																	const defaultCountry = getValues()?.country;
																	const hasSelectedDefault = val.some(({ code }) => code === defaultCountry?.code);
																	this.setState({ allowedCountries: val });

																	if (!hasSelectedDefault) {
																		setValue('country', null);
																	}

																	trigger('country');
																	trigger('allowedCountryForPhoneNumbers');
																}}
																onBlur={() => {
																	trigger('country');
																	trigger('allowedCountryForPhoneNumbers');
																}}
															/>
														)
													}}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="country"
													control={control}
													defaultValue={null}
													rules={{
														required: 'Field should not be empty',
													}}
													render={({ onChange, value }) => {
														return (
															<Autocomplete
																options={this.state.allowedCountries}
																autoComplete
																includeInputInList
																value={value}
																renderInput={(params) => {
																	return (
																		<CustomTextField {...params}
																			error={!!errors.country}
																			helperText={errors.country ? errors.country.message : ""}
																			label="Default Country *"
																			margin="normal" />
																	)
																}}
																getOptionLabel={({ label }) => label}
																onChange={(e, val) => {
																	onChange(val);
																	trigger('country');
																}}
																onBlur={() => {
																	trigger('country');
																}}
															/>
														)
													}}
												/>
											</GridItem>
											<GridItem md={12}>
												<h4 className={classes.subTitle}>Login</h4>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="autoLogoutNotificationTimeout"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.autoLogoutNotificationTimeout.min,
															message: `Min value is ${validation.autoLogoutNotificationTimeout.min}`
														},
														max: {
															value: validation.autoLogoutNotificationTimeout.max,
															message: `Max value is ${validation.autoLogoutNotificationTimeout.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Show Auto Logout Message in (minutes) *'
															error={!!errors.autoLogoutNotificationTimeout}
															helperText={errors.autoLogoutNotificationTimeout ? errors.autoLogoutNotificationTimeout.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.autoLogoutNotificationTimeout.min,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('autoLogoutNotificationTimeout');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="autoLogoutTimeout"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.autoLogoutTimeout.min,
															message: `Min value is ${validation.autoLogoutTimeout.min}`
														},
														max: {
															value: validation.autoLogoutTimeout.max,
															message: `Max value is ${validation.autoLogoutTimeout.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Logout Dialog Duration (minutes) *'
															error={!!errors.autoLogoutTimeout}
															helperText={errors.autoLogoutTimeout ? errors.autoLogoutTimeout.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.autoLogoutTimeout.min,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('autoLogoutTimeout');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem md={12}>
												<h4 className={classes.subTitle}>Contact Center</h4>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="contactCenterNameMaxLength"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.contactCenterNameMaxLength.min,
															message: `Min value is ${validation.contactCenterNameMaxLength.min}`
														},
														max: {
															value: validation.contactCenterNameMaxLength.max,
															message: `Max value is ${validation.contactCenterNameMaxLength.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Contact Center Name Length *'
															error={!!errors.contactCenterNameMaxLength}
															helperText={errors.contactCenterNameMaxLength ? errors.contactCenterNameMaxLength.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.contactCenterNameMaxLength.min,
																max: validation.contactCenterNameMaxLength.max,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('contactCenterNameMaxLength');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="defaultNotificationMaxLength"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.defaultNotificationMaxLength.min,
															message: `Min value is ${validation.defaultNotificationMaxLength.min}`
														},
														max: {
															value: validation.defaultNotificationMaxLength.max,
															message: `Max value is ${validation.defaultNotificationMaxLength.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Default Pre-Call Notification Length *'
															error={!!errors.defaultNotificationMaxLength}
															helperText={errors.defaultNotificationMaxLength ? errors.defaultNotificationMaxLength.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.defaultNotificationMaxLength.min,
																max: validation.defaultNotificationMaxLength.max,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('defaultNotificationMaxLength');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="unverifiedCallArrivalNotificationMaxLength"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.unverifiedCallArrivalNotificationMaxLength.min,
															message: `Min value is ${validation.unverifiedCallArrivalNotificationMaxLength.min}`
														},
														max: {
															value: validation.unverifiedCallArrivalNotificationMaxLength.max,
															message: `Max value is ${validation.unverifiedCallArrivalNotificationMaxLength.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Unverified Call Arrival Notification Length *'
															error={!!errors.unverifiedCallArrivalNotificationMaxLength}
															helperText={errors.unverifiedCallArrivalNotificationMaxLength ? errors.unverifiedCallArrivalNotificationMaxLength.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.unverifiedCallArrivalNotificationMaxLength.min,
																max: validation.unverifiedCallArrivalNotificationMaxLength.max,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('unverifiedCallArrivalNotificationMaxLength');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="customNotificationMaxLength"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.customNotificationMaxLength.min,
															message: `Min value is ${validation.customNotificationMaxLength.min}`
														},
														max: {
															value: validation.customNotificationMaxLength.max,
															message: `Max value is ${validation.customNotificationMaxLength.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Custom Call Purpose Length *'
															error={!!errors.customNotificationMaxLength}
															helperText={errors.customNotificationMaxLength ? errors.customNotificationMaxLength.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.customNotificationMaxLength.min,
																max: validation.customNotificationMaxLength.max,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('customNotificationMaxLength');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem md={12}>
												<h4 className={classes.subTitle}>Analytics</h4>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="keepCallsPeriodInDays"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.keepCallsPeriodInDays.min,
															message: `Min value is ${validation.keepCallsPeriodInDays.min}`
														},
														max: {
															value: validation.keepCallsPeriodInDays.max,
															message: `Max value is ${validation.keepCallsPeriodInDays.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Store Analytics Data (number of days) *'
															error={!!errors.keepCallsPeriodInDays}
															helperText={errors.keepCallsPeriodInDays ? errors.keepCallsPeriodInDays.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.keepCallsPeriodInDays.min,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('keepCallsPeriodInDays');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="refreshAnalyticsDataIntervalInSeconds"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.refreshAnalyticsDataIntervalInSeconds.min,
															message: `Min value is ${validation.refreshAnalyticsDataIntervalInSeconds.min}`
														},
														max: {
															value: validation.refreshAnalyticsDataIntervalInSeconds.max,
															message: `Max value is ${validation.refreshAnalyticsDataIntervalInSeconds.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Refresh Data Interval (seconds) *'
															error={!!errors.refreshAnalyticsDataIntervalInSeconds}
															helperText={errors.refreshAnalyticsDataIntervalInSeconds ? errors.refreshAnalyticsDataIntervalInSeconds.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.refreshAnalyticsDataIntervalInSeconds.min,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('refreshAnalyticsDataIntervalInSeconds');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="keepRatingsPeriodInDays"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.keepRatingsPeriodInDays.min,
															message: `Min value is ${validation.keepRatingsPeriodInDays.min}`
														},
														max: {
															value: validation.keepRatingsPeriodInDays.max,
															message: `Max value is ${validation.keepRatingsPeriodInDays.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Store Ratings Data (number of days) *'
															error={!!errors.keepRatingsPeriodInDays}
															helperText={errors.keepRatingsPeriodInDays ? errors.keepRatingsPeriodInDays.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.keepRatingsPeriodInDays.min,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('keepRatingsPeriodInDays');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem xs={12} sm={12} md={6}>
												<Controller
													name="refreshThresholdsIntervalInMinutes"
													control={control}
													defaultValue={0}
													rules={{
														required: 'Field should not be empty',
														min: {
															value: validation.refreshThresholdsIntervalInMinutes.min,
															message: `Min value is ${validation.refreshThresholdsIntervalInMinutes.min}`
														},
														max: {
															value: validation.refreshThresholdsIntervalInMinutes.max,
															message: `Max value is ${validation.refreshThresholdsIntervalInMinutes.max}`
														},
													}}
													render={(props) => (
														<CustomInput
															labelText='Refresh Thresholds Interval (minutes) *'
															error={!!errors.refreshThresholdsIntervalInMinutes}
															helperText={errors.refreshThresholdsIntervalInMinutes ? errors.refreshThresholdsIntervalInMinutes.message : ""}
															formControlProps={{
																fullWidth: true
															}}
															inputProps={{
																type: 'number',
																min: validation.refreshThresholdsIntervalInMinutes.min,
																value: props.value,
															}}
															onChange={(event) => {
																const value = this.convertToIntValue(event.target.value);
																event.target.value = value;
																props.onChange(value);
																trigger('refreshThresholdsIntervalInMinutes');
															}}
														/>
													)}
												/>
											</GridItem>
											<GridItem md={12}>
                                                                                                <h4 className={classes.subTitle}>SDK Settings</h4>
                                                                                        </GridItem>
                                                                                        <GridItem xs={12} sm={12} md={6}>
												<span className={classes.feature}>Fraud Protection</span>
												<span className={classes.feature}>    </span>
                                                                                                <Controller
                                                                                                        name="enableFraudProtection"
                                                                                                        control={control}
                                                                                                        defaultValue={this.state.enableFraudProtection === 'true'}
                                                                                                        render={(props) => (
														<FormControlLabel
															control={
																<Switch
																	checked={this.state.enableFraudProtection === 'true'}
																	onChange={(event) => {
																		const value = (event.target.checked ? 'true' : 'false');
																		this.setState({ enableFraudProtection: value });

																		props.onChange(value);
																		trigger('enableFraudProtection');
																	}}
																	value={this.state.enableFraudProtection}
																	defaultChecked
																/>
															}
															label={this.state.enableFraudProtection === 'true' ? 'Enabled' : 'Disabled'}
														/>
													)}
                                                                                                />
                                                                                        </GridItem>
										</GridContainer>
										{
											<div>
												<div className={classes.phantomFooter} />
												<div className={classes.footer}>
													<Button
														color='success'
														size='sm'
														type='submit'
														className={classes.saveButton}
														disabled={
															!!Object.keys(errors).length
															|| isSubmitting
															|| !isDirty
														}
													>Save</Button>
												</div>
											</div>
										}
									</form>
								</CardBody>
							</Card>
						</GridItem>
					</GridContainer >
					<RouteLeavingGuard
						when={isDirty}
						navigate={(path) => history.push(path)}
						shouldBlockNavigation={() => isDirty}
					/>
				</>
		);
	}
}

const mapStateToProps = ({ settings }) => {
	return {
		countriesCodesList: settings?.allowedCountryForPhoneNumbers,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch
	),
	setPortalSettings: bindActionCreators(
		setPortalSettingsActionCreator,
		dispatch
	),
	setAutoLockSettings: bindActionCreators(
		setAutoLockSettingsActionCreator,
		dispatch
	),
});

export default compose(
	withStyles(styles),
	connect(mapStateToProps, mapDispatchToProps),
	withRouter,
	withSnackbar,
	withForm,
)(Configuration);
